import { AxiosError } from 'axios';
import { apiClient } from './client';

// agent-server /internal/image-presets 를 admin-server 가 그대로 넘긴다.
// 모양: agent-server/src/agent_server/schemas/image_preset_admin.py

export const ASPECTS = ['source', '3:4', '4:5', '9:16', '1:1', '4:3'] as const;
export type Aspect = (typeof ASPECTS)[number];

// 서버 output_size 와 같은 값. 화면에 실제 캔버스 크기를 보여 주는 데만 쓴다.
export const CANVAS: Record<Exclude<Aspect, 'source'>, [number, number]> = {
  '3:4': [880, 1184],
  '4:5': [912, 1152],
  '9:16': [768, 1360],
  '1:1': [1024, 1024],
  '4:3': [1184, 880],
};

export type Provider = 'openai' | 'gemini';

/** 저장하는 필드. id 는 만든 뒤 못 바꾼다. */
export interface PresetFields {
  id: string;
  name: string;
  category: string;
  description: string;
  prompt: string;
  negative_prompt: string | null;
  variables: string[];
  allow_text_in_image: boolean;
  aspect: Aspect;
  reference_images: string[];
  provider: Provider;
  model: string;
  quality: string | null;
  enabled: boolean;
  order: number;
}

export interface AdminPreset extends PresetFields {
  version: number;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
}

export interface PresetCategory {
  id: string;
  name: string;
  order: number;
}

export interface ReferenceImage {
  name: string;
  mime: string;
  size: number;
  sha256: string;
  created_by: string | null;
  created_at: string;
  used_by: string[];
}

export interface AdminCatalog {
  categories: PresetCategory[];
  presets: AdminPreset[];
  reference_images: ReferenceImage[];
}

export interface PresetVersion {
  version: number;
  /** 그 시점 행 전체. DB 모양이라 분류는 category_id. */
  snapshot: Record<string, unknown>;
  note: string | null;
  created_by: string | null;
  created_at: string;
}

export interface ImportReport {
  dry_run: boolean;
  presets: { new: string[]; same: string[]; conflict: string[] };
  categories: { new: string[]; existing: string[] };
  reference_images: { new: string[]; same: string[]; conflict: string[] };
}

/** provider 응답 원문 중 화면이 읽는 부분. 나머지 필드도 그대로 들어 있다. */
export interface ProviderBody {
  size?: string;
  usage?: { total_tokens?: number };
  data?: { b64_json?: string }[];
  candidates?: { content?: { parts?: { inline_data?: { data?: string }; inlineData?: { data?: string } }[] } }[];
  [key: string]: unknown;
}

export interface TrialResult {
  result: ProviderBody;
  provider_request_id: string | null;
}

export interface FieldError {
  loc: string[];
  msg: string;
}

/** 서버 오류 본문: { detail, error_code, errors?, current?, used_by?, report? } */
export interface PresetApiError {
  detail?: string;
  error_code?: string;
  errors?: FieldError[];
  current?: AdminPreset;
  used_by?: string[];
  report?: ImportReport;
}

export const presetError = (error: unknown): PresetApiError | null =>
  error instanceof AxiosError ? ((error.response?.data as PresetApiError) ?? null) : null;

const BASE = '/image-presets';
// 클라이언트 기본 Content-Type 이 JSON 이라, 그대로 두면 axios 가 FormData 를 JSON 으로 바꿔 보낸다.
const MULTIPART = { 'Content-Type': 'multipart/form-data' };
const enc = encodeURIComponent;

export const imagePresetsApi = {
  list: async (): Promise<AdminCatalog> => (await apiClient.get<AdminCatalog>(BASE)).data,

  create: async (fields: Partial<PresetFields>, note?: string): Promise<AdminPreset> =>
    (await apiClient.post<AdminPreset>(BASE, { fields, note })).data,

  update: async (id: string, version: number, changes: Partial<PresetFields>, note?: string): Promise<AdminPreset> =>
    (await apiClient.patch<AdminPreset>(`${BASE}/${enc(id)}`, { version, changes, note })).data,

  remove: async (id: string, version: number): Promise<void> => {
    await apiClient.delete(`${BASE}/${enc(id)}`, { params: { version } });
  },

  reorder: async (category: string, ids: string[]): Promise<AdminPreset[]> =>
    (await apiClient.post<AdminPreset[]>(`${BASE}/reorder`, { category, ids })).data,

  versions: async (id: string): Promise<PresetVersion[]> =>
    (await apiClient.get<PresetVersion[]>(`${BASE}/${enc(id)}/versions`)).data,

  rollback: async (id: string, toVersion: number, version: number): Promise<AdminPreset> =>
    (await apiClient.post<AdminPreset>(`${BASE}/${enc(id)}/rollback`, { to_version: toVersion, version })).data,

  createCategory: async (category: PresetCategory): Promise<PresetCategory> =>
    (await apiClient.post<PresetCategory>(`${BASE}/categories`, category)).data,

  updateCategory: async (id: string, changes: Partial<Omit<PresetCategory, 'id'>>): Promise<PresetCategory> =>
    (await apiClient.patch<PresetCategory>(`${BASE}/categories/${enc(id)}`, changes)).data,

  deleteCategory: async (id: string): Promise<void> => {
    await apiClient.delete(`${BASE}/categories/${enc(id)}`);
  },

  /** 미리보기용. <img src> 로는 인증 헤더를 못 실어 blob 으로 받는다. */
  referenceBlob: async (name: string): Promise<Blob> =>
    (await apiClient.get(`${BASE}/reference-images/${enc(name)}`, { responseType: 'blob' })).data,

  putReference: async (name: string, file: File): Promise<ReferenceImage> => {
    const form = new FormData();
    form.append('file', file);
    return (await apiClient.put<ReferenceImage>(`${BASE}/reference-images/${enc(name)}`, form, { headers: MULTIPART }))
      .data;
  },

  deleteReference: async (name: string): Promise<void> => {
    await apiClient.delete(`${BASE}/reference-images/${enc(name)}`);
  },

  /** 저장 전 편집 내용으로 실제 생성을 한 번 돌린다. 비용이 든다. */
  trial: async (
    preset: Partial<PresetFields>,
    images: File[],
    inputs: Record<string, string>,
  ): Promise<TrialResult> => {
    const form = new FormData();
    form.append('preset', JSON.stringify(preset));
    images.forEach((image) => form.append('images', image));
    if (Object.keys(inputs).length) form.append('inputs', JSON.stringify(inputs));
    return (
      await apiClient.post<TrialResult>(`${BASE}/test-generations`, form, { headers: MULTIPART, timeout: 180_000 })
    ).data;
  },

  exportBundle: async (ids: string[]): Promise<Record<string, unknown>> =>
    (await apiClient.get(`${BASE}/export`, { params: { ids: ids.join(',') } })).data,

  importBundle: async (
    bundle: Record<string, unknown>,
    options: { dryRun: boolean; overwrite?: string[]; overwriteImages?: string[] },
  ): Promise<ImportReport> =>
    (
      await apiClient.post<ImportReport>(
        `${BASE}/import`,
        { bundle, overwrite: options.overwrite ?? [], overwrite_images: options.overwriteImages ?? [] },
        { params: { dry_run: options.dryRun } },
      )
    ).data,
};
